import {Animation, Character, Combined, Layer, Transformable} from "../../../feature/project/Shot";
import {Button, Flex, Input, Modal, Space, Tabs, Typography} from "antd";
import React, {useCallback, useEffect, useState} from "react";
import {ShotState} from "./ShotViewport";
import {Status} from "../../../feature/project/status";
import {fetchTags} from "../../../feature/project/projectsSlice";
import {Tag} from "../../../feature/project/fields";
import {useAppDispatch, useAppSelector} from "../../../feature/tools";
import {useParams} from "react-router-dom";
import {TagFlow} from "../../../components/TagFlow";
import LayerList from "../../common/LayerList";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faPlay, faPlus, faStop} from "@fortawesome/free-solid-svg-icons";
import {deleteLayer, saveCharacterLayer, saveLayer} from "../../../feature/project/layerSlice";
import {Starfield} from "../../../components/Starfiled";
import {LayerEditModal} from "./LayerEditModal";
import {ElementProperties} from "./ElementProperties";
import {ElementsCollection} from "../../common/ElementsCollection";
import {ElementInfo} from "./ElementInfo";
import {
    addCharacterTag,
    fetchCharactersCollection,
    fetchCharactersLore,
    removeCharacterTag,
    saveCharacter
} from "../../../feature/project/charactersSlice";
import {AnimationForm, compareAnimations} from "./AnimationForm";
import {deleteAnimation, saveCharacterAnimation} from "../../../feature/project/animationsSlice";
import {AnimatableElement} from "./AnimatableElement";
import {EditElement} from "./EditElement";

const {confirm} = Modal;

export const CharacterEditModal = (
    props: {
        character: Character,
        onSave: (character: Character) => void,
        onCancel: () => void
    }
) => {
    const dispatch = useAppDispatch()
    const {projectId, shotId} = useParams()
    const [title, setTitle] = React.useState(props.character.title)
    const [tags, setTags] = React.useState<Array<Tag>>(props.character.tags || [])
    const [showSelectCharacter, setShowSelectCharacter] = React.useState(false)

    const [hasChanges, setHasChanges] = React.useState(false)

    useEffect(() => {
        setHasChanges(title !== props.character.title)
    }, [title])

    useEffect(() => {
        setTitle(props.character.title)
        setTags(props.character.tags || [])
    }, [props.character, props.character.tags])

    useEffect(() => {
        if (!props.character.id) {
            setShowSelectCharacter(true)
        }
    }, [props.character.id])

    return (
        <>
            <Modal
                open={true}
                title={props.character.id ? 'Edit Character' : 'Add haracter'}
                width={1000}
                okText={'Save'}
                cancelText={'Close'}
                onCancel={props.onCancel}
                okButtonProps={{disabled: !hasChanges}}
                onOk={() => props.onSave({
                    ...props.character,
                    title: title,
                    tags: tags
                })}
            >
                {showSelectCharacter ?
                    <CharacterSelectView onCharacterSelected={(character) => {
                        if (projectId && shotId) {
                            const char = {
                                ...character,
                                id: undefined,
                                shot: {
                                    id: parseInt(shotId)
                                },
                                layers: character?.layers?.map(layer => {
                                    return {
                                        ...layer,
                                        id: undefined,
                                        animations: layer.animations?.map(anim => {
                                            return {...anim, id: undefined}
                                        })
                                    }
                                })
                            }
                            dispatch(saveCharacter({projectId: parseInt(projectId), character: char as Character}))
                            setShowSelectCharacter(false)
                        }
                    }}/>
                    :
                    <CharacterEditView
                        character={props.character}
                        title={title}
                        tags={tags}
                        onTitleChanged={(value) => setTitle(value)}
                        onTagsChanged={(value) => setTags(value)}
                    />}
            </Modal>
        </>
    )
}

const CharacterSelectView = (props: { onCharacterSelected: (character: Character) => void }) => {
    const dispatch = useAppDispatch()
    const {projectId} = useParams()
    const {storyId} = useParams()
    const charactersCollection = useAppSelector(state => state.characters.collection)
    const charactersLore = useAppSelector(state => state.characters.lore)
    const status = useAppSelector(state => state.characters.status)

    useEffect(() => {
        if (status === Status.Idle && storyId && projectId) {
            dispatch(fetchCharactersCollection({projectId: parseInt(projectId), storyId: parseInt(storyId)}))
            dispatch(fetchCharactersLore({projectId: parseInt(projectId), storyId: parseInt(storyId)}))
        }
    }, [status, storyId, projectId])

    return (
        <div style={{height: 400}}>
            <Tabs
                defaultActiveKey="1"
                items={[
                    {
                        label: `Collection`,
                        key: "1",
                        children: <ElementsCollection
                            animated={true}
                            key={"1"} elements={charactersCollection || []}
                            onSelect={(element) => {
                                props.onCharacterSelected(element as Character)
                            }}/>,
                    },
                    {
                        label: `Lore`,
                        key: "2",
                        children: <ElementsCollection
                            animated={false}
                            key={"2"}
                            elements={charactersLore || []}
                            onSelect={(element: any) => {
                                props.onCharacterSelected(
                                    {title: (element as Character).title, component: element?.component}
                                )
                            }}/>,
                    },
                ]}
            />
        </div>
    )
}

const CharacterEditView = (props: {
    character: Character,
    title?: string,
    tags: Array<Tag>,
    onTitleChanged: (value: string) => void,
    onTagsChanged: (value: Array<Tag>) => void
}) => {
    const status = useAppSelector(state => state.characters.status)
    const dispatch = useAppDispatch()
    const {projectId, storyId} = useParams()
    const [scale, setScale] = React.useState(1)
    const [height, setHeight] = React.useState(0)
    const [width, setWidth] = React.useState(0)
    const allTags = useAppSelector(state => state.projects.tags)
    const [layer, setLayer] = React.useState<Combined | undefined>(undefined)
    const [showEditLayerModal, setShowEditLayerModal] = React.useState<Layer | undefined>(undefined)
    const [play, setPlay] = React.useState(false)
    const [played, setPlayed] = useState<Set<number>>(new Set())
    const [info, setInfo] = React.useState({x: 0, y: 0, z: 0, scale: 1})
    const [animation, setAnimation] = React.useState<Animation | undefined>(undefined)
    const [selectPivotPoint, setSelectPivotPoint] = React.useState(false)

    useEffect(() => {
        if (layer) {
            const selectedLayer: Layer | undefined = props.character.layers?.find(lay => lay.id === layer?.id)
            setLayer({...selectedLayer, type: 'Layer'})
        }
        if (showEditLayerModal) {
            const editedLayer: Layer | undefined = props.character.layers?.find(lay => lay.id === showEditLayerModal?.id)
            setShowEditLayerModal({...editedLayer, type: 'Layer'})
        }
    }, [props.character])

    useEffect(() => {
        setInfo({x: layer?.x || 0, y: layer?.y || 0, z: layer?.z || 0, scale: layer?.scale || 1})
    }, [layer])

    useEffect(() => {
        setScale(width > height ? width / 1920 : height / 1920)
    }, [width, height, props.character])

    useEffect(() => {
        const objectAnimationsCount = props.character.animations?.length ?? 0
        const layersAnimationsCount = props.character.layers?.reduce((acc, curr) => acc + (curr.animations?.length || 0), 0) ?? 0
        if (played.size === (objectAnimationsCount + layersAnimationsCount + 1) && play) {
            setPlay(false)
            setPlayed(new Set())
        }
    }, [played, play]);

    useEffect(() => {
        if (status === Status.Idle && projectId) {
            if (props.character.id) {
                dispatch(fetchTags({projectId: parseInt(projectId)}))
            }
        }
    }, [status, dispatch])

    const ref = useCallback((node: HTMLDivElement) => {
        if (!node) return;
        const resizeObserver = new ResizeObserver(() => {
            setWidth(node.clientWidth || 0)
            setHeight(node.clientHeight || 0)
        });
        resizeObserver.observe(node);
    }, []);

    const showConfirmLayerDeletion = (item: Layer) => {
        confirm({
            title: 'Delete Layer',
            content: 'Are you sure you want to delete this layer? It will also delete all the animations associated with it.',
            icon: <></>,
            okText: 'Yes, delete',
            okButtonProps: {danger: true, type: 'primary'},
            cancelText: 'No',
            onOk() {
                if (projectId && item.id) {
                    dispatch(deleteLayer({projectId: parseInt(projectId), layerId: item.id}))
                }
            },
        });
    }

    const showConfirmAnimationDeletion = (item: Animation) => {
        confirm({
            title: 'Delete Animation',
            content: 'Are you sure you want to delete this animation?',
            icon: <></>,
            okText: 'Yes, delete',
            okButtonProps: {danger: true, type: 'primary'},
            cancelText: 'No',
            onOk() {
                if (projectId && item.id) {
                    dispatch(deleteAnimation({
                        projectId: parseInt(projectId),
                        animationId: item.id
                    }))
                    setAnimation(undefined)
                }
            },
        });
    }

    return (
        <>
            <Flex gap={16}>
                <Flex align={'center'} style={{
                    minWidth: 400,
                    height: 400,
                    borderRadius: 6,
                    clipPath: 'border-box',
                    position: "relative",
                    background: '#eeeeee'
                }}>
                    <div
                        ref={ref}
                        style={{
                            position: 'relative',
                            width: '100%',
                            height: 'calc(100% / 1.77)'
                        }}>
                        {props.character.layers && props.character.layers.length > 0 ?
                            <div style={{
                                width: 1920,
                                height: 1080,
                                overflow: 'hidden',
                                background: '#000',
                                position: "absolute",
                                top: 0,
                                left: 0,
                                scale: `${scale}`,
                                transformOrigin: 'top left'
                            }}>
                                <AnimatableElement
                                    element={{...props.character, x: 0, y: 0, z: 0, scale: 1}}
                                    play={play}
                                    scale={1}
                                    animations={props.character.animations && props.character.animations.length > 0 ? props.character.animations : []}
                                    onPlayEnd={(id: number) => {
                                        if (play) {
                                            setPlayed(prev => new Set(prev).add(id))
                                        }
                                    }}>

                                    {props.character.layers && props.character.layers.length > 0 ?
                                        props.character.layers.map(lay => {
                                            return <EditElement
                                                key={lay.id}
                                                prefix={"character-edit"}
                                                element={lay}
                                                play={play}
                                                onPlayEnd={(id) => {
                                                    if (play) {
                                                        setPlayed(prev => new Set(prev).add(id))
                                                    }
                                                }}
                                                selectedElement={layer}
                                                scale={scale}
                                                onElementSelected={(element) => {
                                                    setLayer(element)
                                                }}
                                                onMove={(info: { x: number, y: number, z: number, scale: number }) =>
                                                    setInfo(info)
                                                }
                                            />
                                        }) : ''
                                    }
                                </AnimatableElement>
                            </div> :
                            <div style={{
                                width: '100%',
                                height: '100%',
                                background: "#000000",
                                position: "relative"
                            }}>
                                <Starfield
                                    id={"object-edit-starfield"}
                                    starCount={5000}
                                    starColor={[255, 255, 255]}
                                    speedFactor={0.1}
                                    backgroundColor="black"
                                    scale={scale}/></div>
                        }
                    </div>
                    <Button icon={<FontAwesomeIcon icon={play ? faStop : faPlay}/>}
                            onClick={() => setPlay(!play)}
                            style={{
                                lineHeight: '12px',
                                position: 'absolute',
                                top: 10,
                                right: 10,
                                zIndex: 10000,
                                width: 38
                            }}
                            disabled={props.character.animations?.length == 0 && (!props.character.layers || props.character.layers.reduce((acc, curr) => acc + (curr.animations?.length || 0), 0) === 0)}
                    />
                    {layer ?
                        <>
                            <ElementProperties
                                style={{
                                    zIndex: 100000,
                                    position: 'absolute',
                                    right: 10,
                                    top: 52,
                                    background: '#fff',
                                    borderRadius: 6,
                                    padding: 2,
                                    bottom: 10
                                }}
                                element={layer}
                                onScaleChanged={(value) => {
                                    setLayer(
                                        {
                                            ...layer,
                                            scale: value
                                        } as Transformable
                                    )
                                    setInfo({...info, scale: value})
                                }}
                                onTransformed={(element) => {
                                    if (projectId && element && storyId) {
                                        dispatch(saveLayer({
                                            projectId: parseInt(projectId),
                                            storyId: parseInt(storyId),
                                            layer: element as Layer
                                        }))
                                    }
                                }}
                                onAlignmentChanged={() => {
                                }}
                            />
                            <ElementInfo element={info}/>
                        </> : ''}
                </Flex>
                <Space direction={'vertical'} style={{width: '100%', maxHeight: 400, overflowY: 'auto', scrollbarWidth: "none"}}
                       size={16}>
                    <div>
                        <Typography style={{fontWeight: 700}}>Title</Typography>
                        <Input value={props.title} onChange={(e) => props.onTitleChanged(e.target.value)}/>
                    </div>
                    <div>
                        <Typography style={{fontWeight: 700}}>Tags</Typography>
                        <TagFlow allTags={allTags}
                                 tags={props.tags}
                                 onTagAdded={(tag) => {
                                     props.onTagsChanged([...props.tags, tag])
                                     if (props.character.id && projectId) {
                                         dispatch(addCharacterTag({
                                             projectId: parseInt(projectId),
                                             characterId: props.character.id,
                                             tag: tag
                                         }))
                                     }
                                 }}
                                 onTagDeleted={(tag) => {
                                     if (tag.id && props.character.id && projectId) {
                                         dispatch(removeCharacterTag({
                                             projectId: parseInt(projectId),
                                             characterId: props.character.id,
                                             tagId: tag.id
                                         }))
                                     }
                                 }}
                        />
                    </div>
                    <Flex align={'start'} gap={16}>
                        <Space direction={'vertical'}
                               style={{width: animation ? 300 : '100%', maxHeight: 400, overflowY: 'auto', scrollbarWidth: "none"}}
                        >
                            <div>
                                <Flex justify={'space-between'} className={'layer-group-title'} align={'center'}>
                                    <Typography style={{fontWeight: 700}}>Animations</Typography>
                                    <Button
                                        icon={<FontAwesomeIcon icon={faPlus}/>}
                                        type={'text'}
                                        onClick={() => {
                                            setAnimation({})
                                        }}
                                    />
                                </Flex>

                                <LayerList
                                    is={(item) => animation?.id === item.id}
                                    data={props.character.animations}
                                    title={(item) => item.title}
                                    clickable={true}
                                    onItemClick={(item) => {
                                        setAnimation(item)

                                    }}
                                    onDeleteClick={(item) => {
                                        showConfirmAnimationDeletion(item)
                                    }}
                                    onEditClick={(item) => {
                                        setAnimation(item)
                                    }}
                                />
                            </div>
                            <div>
                                <Flex justify={'space-between'} className={'layer-group-title'} align={'center'}>
                                    <Typography style={{fontWeight: 700}}>Layers</Typography>
                                    <Button
                                        icon={<FontAwesomeIcon icon={faPlus}/>}
                                        type={'text'}
                                        onClick={() =>
                                            setShowEditLayerModal({})
                                        }
                                    />
                                </Flex>

                                <LayerList
                                    is={(item) => layer?.id === item.id}
                                    data={props.character.layers?.map(lay => {
                                        return layer !== undefined && lay.id === layer.id ? {
                                            ...layer,
                                            layers: [layer],
                                            type: 'Layer'
                                        } as Combined : {...lay, layers: [lay], type: 'Layer'} as Combined
                                    })}
                                    clickable={true}
                                    itemStyle={{padding: 0}}
                                    title={(item, index) => <Flex align={'center'} gap={10}>
                                        <img src={(item as Layer).source}
                                             style={{
                                                 width: 50,
                                                 height: 50,
                                                 borderRadius: '5px',
                                                 objectFit: 'contain',
                                                 background: '#eeeeee'
                                             }}
                                        />
                                        {`Layer ${index + 1}`}
                                    </Flex>}
                                    onItemClick={(item) => {
                                        setLayer(item)
                                    }}
                                    onDeleteClick={(item) => {
                                        showConfirmLayerDeletion(item as Layer)
                                    }}
                                    onEditClick={(item) => {
                                        setLayer(item)
                                        setShowEditLayerModal(item as Layer)
                                    }}
                                />
                            </div>
                        </Space>
                        {animation ?
                            <AnimationForm
                                animation={animation}
                                pivotPoint={selectPivotPoint}
                                onSelectPivotPoint={(select: boolean) => setSelectPivotPoint(select)}
                                wrapper={(children) => {
                                    return <Space direction={'vertical'}
                                                  style={{width: 300, maxHeight: 400, overflowY: 'auto', scrollbarWidth: "none"}}
                                                  size={16}>{children} </Space>
                                }}
                                onSave={(animation: Animation) => {
                                    const origin = props.character.animations?.find(item => item.id === animation?.id)
                                    if (projectId && props.character.id && !compareAnimations(origin, animation)) {
                                        dispatch(
                                            saveCharacterAnimation(
                                                {
                                                    projectId: parseInt(projectId),
                                                    characterId: props.character.id,
                                                    animation: animation
                                                }
                                            )
                                        )
                                    }
                                }}
                                onClose={() => setAnimation(undefined)}
                            />
                            : ''}
                    </Flex>
                </Space>
            </Flex>
            {
                showEditLayerModal ?
                    <LayerEditModal
                        componentId={props.character?.component?.id}
                        key={showEditLayerModal?.id || '' + showEditLayerModal?.animations?.map(an => an.id)?.join('')}
                        layer={showEditLayerModal}
                        onCancel={() => setShowEditLayerModal(undefined)}
                        onSave={(layer) => {
                            if (projectId && layer && storyId && props.character.id) {
                                if (layer.id) {
                                    dispatch(saveLayer({
                                        projectId: parseInt(projectId),
                                        storyId: parseInt(storyId),
                                        layer: layer
                                    }))
                                } else {
                                    dispatch(saveCharacterLayer({
                                        projectId: parseInt(projectId),
                                        characterId: props.character.id,
                                        storyId: parseInt(storyId),
                                        layer: layer
                                    }))
                                }
                            }
                            setShowEditLayerModal(undefined)
                        }}
                    /> : ''
            }
        </>
    )
}